import { resolveActiveWorkspaceContext } from "@/lib/active-workspace";
import {
  assertCurrentConnectorEntitlementScope,
  isConnectorApiRequest,
} from "@/lib/connector-entitlement-scope";
import { useWorkspaceStore } from "@/store/workspace/workspace-store";
import { refreshSession } from "@/services/auth";

export class HttpError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.body = body;
  }
}

export class ApiRequestError extends HttpError {
  constructor(
    message: string,
    readonly errorCode?: string,
    readonly httpStatus?: number,
    body?: unknown,
  ) {
    super(httpStatus ?? 0, message, body);
    this.name = "ApiRequestError";
  }
}

export async function fetcher<T>(url: string, init?: RequestInit): Promise<T> {
  if (isConnectorApiRequest(url)) {
    assertCurrentConnectorEntitlementScope();
  }

  const { workspacesLoaded } = useWorkspaceStore.getState();
  const activeWorkspaceContext = resolveActiveWorkspaceContext();

  // Build headers with workspace context
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...init?.headers,
  } as Record<string, string>;

  // Add workspace headers if workspace is selected
  // Skip for auth endpoints (they don't need workspace context)
  const isAuthEndpoint = url.includes("/auth/v1/auth/");
  const canAttachWorkspaceContext =
    !isAuthEndpoint &&
    Boolean(activeWorkspaceContext.workspaceId) &&
    (activeWorkspaceContext.source === "url" || workspacesLoaded);
  const workspaceId = activeWorkspaceContext.workspaceId;

  if (canAttachWorkspaceContext && workspaceId) {
    headers["X-Workspace-Id"] = workspaceId;

    if (activeWorkspaceContext.organizationId) {
      headers["X-Organization-Id"] = activeWorkspaceContext.organizationId;
    }
  }

  const res = await fetch(url, {
    credentials: "include",
    ...init,
    headers,
  });

  const data = await res.json().catch(() => ({}));

  const shouldRefresh =
    !res.ok && (res.status === 401 || res.status === 403) && !isAuthEndpoint;

  if (shouldRefresh) {
    const retryHeader = new Headers(init?.headers).get("X-Auth-Retry");
    if (retryHeader !== "1") {
      const refreshed = await refreshSession();
      if (refreshed) {
        const nextHeaders = new Headers(headers);
        nextHeaders.set("X-Auth-Retry", "1");
        return fetcher<T>(url, {
          ...init,
          headers: Object.fromEntries(nextHeaders.entries()),
        });
      }
    }
  }

  if (!res.ok) {
    const error = data as { message?: string; error_code?: string };
    throw new ApiRequestError(
      error.message || res.statusText,
      error.error_code,
      res.status,
      data,
    );
  }

  return data as T;
}
